// useAdminNews.ts — Polls /admin/news (Cash B1.9 Railway endpoint).
//
// Authenticates with VITE_ADMIN_API_TOKEN (Bearer token — shared secret,
// never a principal-derived credential). In dev mode, falls back to mock
// data if the endpoint is unavailable.
//
// Source filter: client-side filtering on the `source` field. No backend
// re-fetch on filter change — all items are fetched; the filter narrows
// the visible set in React state.
//
// Refresh: manual "Refresh" button + last-refreshed indicator. No auto-poll
// (news items are RSS-aggregated daily at 06:00 UTC; polling every 30s would
// be pointless and wasteful against an external endpoint).

import { useCallback, useEffect, useRef, useState } from 'react';

// Valid source tags from the Lore-approved copy table (NW-07..NW-13)
export type NewsSource = 'All' | 'DFX' | 'Motoko' | 'II' | 'DFINITY' | 'Forum' | 'Skills';

export interface NewsItem {
  id: string;
  source: NewsSource;
  postedRelative: string;    // e.g. "Six hours ago"
  postedDate: string;        // ISO date e.g. "2026-04-28"
  headline: string;
  excerpt: string;
  url: string;
}

export interface AdminNewsResponse {
  items: NewsItem[];
  lastRefresh: string;       // ISO datetime e.g. "2026-04-28T06:00:00Z"
  nextRefresh: string;       // ISO datetime
  totalLast7d: number;
}

export interface UseAdminNewsReturn {
  items: NewsItem[];
  filteredItems: NewsItem[];
  activeFilter: NewsSource;
  setFilter: (source: NewsSource) => void;
  isLoading: boolean;
  error: string | null;
  lastRefresh: string | null;
  nextRefresh: string | null;
  totalLast7d: number;
  refresh: () => void;
}

// ---------------------------------------------------------------------------
// Mock data (used when endpoint is unavailable or in dev mode)
// ---------------------------------------------------------------------------

const MOCK_ITEMS: NewsItem[] = [
  {
    id: 'nw-1',
    source: 'Motoko',
    postedRelative: 'Six hours ago',
    postedDate: '2026-04-28',
    headline: 'Motoko skill version bump · now requires moc 1.4.0',
    excerpt:
      'The official Skills Registry advanced its Motoko skill pin to moc 1.4.0 with core 2.4.0. DOMINUS remains on the prior pin per the documented anti-drift rule. Review before any Motoko canister change.',
    url: '#',
  },
  {
    id: 'nw-2',
    source: 'DFX',
    postedRelative: 'Nine hours ago',
    postedDate: '2026-04-28',
    headline: 'Security patch in dfx 0.31.1',
    excerpt:
      'Patches a wallet-command privilege escalation present in 0.31.0. DOMINUS is pinned to 0.25.x and is unaffected; documented for awareness only.',
    url: '#',
  },
  {
    id: 'nw-3',
    source: 'II',
    postedRelative: 'Yesterday',
    postedDate: '2026-04-27',
    headline: 'Internet Identity canister upgrade scheduled for May 4',
    excerpt:
      'Anchor migration rollout starts 14:00 UTC. No action required from integrators; existing delegations remain valid through the transition. Mainnet observers should note the maintenance window.',
    url: '#',
  },
  {
    id: 'nw-4',
    source: 'DFINITY',
    postedRelative: 'Yesterday',
    postedDate: '2026-04-27',
    headline: 'Q2 roadmap · Native Bitcoin integration reaches general availability',
    excerpt:
      'The threshold-ECDSA Bitcoin pathway exits beta. DOMINUS is Stripe-only by design; documented for awareness in case future settlement architecture is reconsidered.',
    url: '#',
  },
  {
    id: 'nw-5',
    source: 'DFX',
    postedRelative: 'Two days ago',
    postedDate: '2026-04-26',
    headline: 'dfx 0.32 released · Motoko stdlib bump and improved cycle reporting',
    excerpt:
      'New release ships moc 1.4.0 by default and adds canister-status caching. Confirms the upstream tooling drift; DOMINUS pin holds at 0.25.x until the bundled @icp-sdk/core migration ships.',
    url: '#',
  },
  {
    id: 'nw-6',
    source: 'Skills',
    postedRelative: 'Two days ago',
    postedDate: '2026-04-26',
    headline: 'Skills Registry · two new entries published',
    excerpt:
      'vetkd-encrypted-notes and scalable-leaderboard joined the registry. Neither applies to DOMINUS at present; logged in the registry inventory for future reference.',
    url: '#',
  },
  {
    id: 'nw-7',
    source: 'Forum',
    postedRelative: 'Three days ago',
    postedDate: '2026-04-25',
    headline: 'Cycles-management migration tips · common configuration pitfalls',
    excerpt:
      'Community thread surfacing the common pitfall of relying on auto-top-up without a freezing-threshold safety margin. Cross-check the DOMINUS top-up configuration against the recommendations.',
    url: '#',
  },
  {
    id: 'nw-8',
    source: 'Motoko',
    postedRelative: 'Three days ago',
    postedDate: '2026-04-25',
    headline: 'Motoko 0.13 adds Crypto stdlib · constant-time comparison primitives',
    excerpt:
      'New crypto module ships in the latest compiler with native constant-time equality and secure-random helpers. Notable for any future canister handling secret material.',
    url: '#',
  },
  {
    id: 'nw-9',
    source: 'DFINITY',
    postedRelative: 'Four days ago',
    postedDate: '2026-04-24',
    headline: 'Subnet split announcement · two new application subnets coming online',
    excerpt:
      'Capacity expansion bringing the application-subnet count to a new high. No DOMINUS migration is implied; canister IDs remain stable on the existing subnet.',
    url: '#',
  },
  {
    id: 'nw-10',
    source: 'Forum',
    postedRelative: 'Five days ago',
    postedDate: '2026-04-23',
    headline: 'Proposal NNS-12345 · reduce cycles cost for query calls',
    excerpt:
      'Governance proposal in the discussion phase. If adopted, the per-query cycle cost on application subnets would fall by an order of magnitude. Voting opens at the close of the discussion period.',
    url: '#',
  },
  {
    id: 'nw-11',
    source: 'II',
    postedRelative: 'Six days ago',
    postedDate: '2026-04-22',
    headline: 'Internet Identity · new derivation-origin advisory',
    excerpt:
      'Updated guidance for SPA integrators using non-default derivation origins. DOMINUS uses the canonical identity.ic0.app origin in production; advisory does not apply.',
    url: '#',
  },
  {
    id: 'nw-12',
    source: 'DFINITY',
    postedRelative: 'Six days ago',
    postedDate: '2026-04-22',
    headline: 'Asset canister certification · v3 spec finalized',
    excerpt:
      'The certified-asset response format reaches v3, deprecating v2 over a six-month window. DOMINUS frontend asset canister requires regeneration before the deprecation takes effect.',
    url: '#',
  },
];

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAdminNews(): UseAdminNewsReturn {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [activeFilter, setActiveFilter] = useState<NewsSource>('All');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const [nextRefresh, setNextRefresh] = useState<string | null>(null);
  const [totalLast7d, setTotalLast7d] = useState(0);

  const isMountedRef = useRef(true);

  const fetchNews = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const endpoint = import.meta.env.VITE_ADMIN_NEWS_URL;
    const token = import.meta.env.VITE_ADMIN_API_TOKEN;

    // If endpoint not configured, fall back to mock data in dev mode.
    if (!endpoint) {
      if (!isMountedRef.current) return;
      setItems(MOCK_ITEMS);
      setLastRefresh('2026-04-28T06:00:00Z');
      setNextRefresh('2026-04-29T06:00:00Z');
      setTotalLast7d(12);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`News endpoint returned ${response.status}`);
      }

      const data: AdminNewsResponse = await response.json();

      if (!isMountedRef.current) return;

      setItems(data.items);
      setLastRefresh(data.lastRefresh);
      setNextRefresh(data.nextRefresh);
      setTotalLast7d(data.totalLast7d);
    } catch (err) {
      if (!isMountedRef.current) return;

      // Fall back to mock data on fetch failure; surface a non-blocking error.
      const isDev = import.meta.env.VITE_DFX_NETWORK !== 'ic';
      if (isDev) {
        setItems(MOCK_ITEMS);
        setLastRefresh('2026-04-28T06:00:00Z');
        setNextRefresh('2026-04-29T06:00:00Z');
        setTotalLast7d(12);
        console.warn('[useAdminNews] Endpoint unavailable; showing mock data.', err);
      } else {
        const message =
          err instanceof Error ? err.message : 'One source is temporarily silent.';
        setError(message);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    fetchNews();
  }, [fetchNews]);

  const setFilter = useCallback((source: NewsSource) => {
    setActiveFilter(source);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchNews();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchNews]);

  // Client-side filter — no re-fetch on filter change.
  const filteredItems =
    activeFilter === 'All'
      ? items
      : items.filter((item) => item.source === activeFilter);

  // Spell out item count in editorial prose register (NW-06 pattern).
  // Up to twelve spelled out; beyond that use digits. (Lore note on NW-06)
  const SPELLED_OUT = [
    'Zero', 'One', 'Two', 'Three', 'Four', 'Five',
    'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
  ] as const;

  const spelledOutTotalLast7d =
    totalLast7d <= 12
      ? SPELLED_OUT[totalLast7d]
      : String(totalLast7d);

  return {
    items,
    filteredItems,
    activeFilter,
    setFilter,
    isLoading,
    error,
    lastRefresh,
    nextRefresh,
    totalLast7d,
    // Override with spelled-out string for rendering (NW-06 approved pattern)
    ...{ spelledOutTotalLast7d },
    refresh,
  } as UseAdminNewsReturn & { spelledOutTotalLast7d: string };
}
